import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { Chip, Stack, Typography } from '@mui/material';

import { type EhrOrganizationSummary, useReadEhrSummariesMutation } from '../../store/ehrApi';

const SummaryContext = createContext<{
  organizations: EhrOrganizationSummary[];
  loading: boolean;
  failed: boolean;
}>({ organizations: [], loading: false, failed: true });

/** One database-only request for the currently displayed organizations. */
export function EhrFleetSummaryProvider({
  organizationIds,
  revision,
  children,
  enabled = true,
}: {
  organizationIds: string[];
  revision?: number;
  children: ReactNode;
  enabled?: boolean;
}) {
  const [read] = useReadEhrSummariesMutation();
  const [state, setState] = useState<{
    key: string;
    organizations: EhrOrganizationSummary[];
    failed: boolean;
  }>();
  const [refresh, setRefresh] = useState(0);
  const key = JSON.stringify(organizationIds.slice(0, 100));
  useEffect(() => {
    if (!enabled || key === '[]') return;
    let active = true;
    const pending = read({
      ehrOrganizationSummaryRequest: { organization_ids: JSON.parse(key) as string[] },
    });
    void pending.unwrap().then(
      result => {
        if (active) setState({ key, organizations: result.organizations, failed: false });
      },
      () => {
        if (active) setState({ key, organizations: [], failed: true });
      },
    );
    return () => {
      active = false;
      pending.abort();
    };
  }, [enabled, key, read, refresh, revision]);
  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => setRefresh(value => value + 1), 30_000);
    return () => clearInterval(timer);
  }, [enabled]);
  const current = enabled && state?.key === key ? state : undefined;
  return (
    <SummaryContext.Provider
      value={{
        organizations: current?.organizations ?? [],
        loading: enabled && !current,
        failed: current?.failed ?? !enabled,
      }}
    >
      {children}
    </SummaryContext.Provider>
  );
}

export function EhrOrganizationStatus({ organizationId }: { organizationId: string }) {
  const { organizations, loading, failed } = useContext(SummaryContext);
  const summary = organizations.find(item => item.organization_id === organizationId);
  return <EhrProgressStatus summary={summary} loading={loading} failed={failed} />;
}

export function EhrProgressStatus({
  summary,
  loading = false,
  failed = false,
}: {
  summary?: EhrOrganizationSummary;
  loading?: boolean;
  failed?: boolean;
}) {
  const label = loading
    ? 'Loading status…'
    : failed || !summary
      ? 'Status unavailable'
      : summary.status === 'completed'
        ? 'Completed'
        : summary.status === 'in_progress'
          ? 'In progress'
          : summary.inventory_pending
            ? 'Checking saved files…'
            : 'No files uploaded';
  return (
    <Stack spacing={0.5}>
      <Chip
        size="small"
        variant="outlined"
        label={label}
        color={!failed && summary?.status === 'completed' ? 'success' : 'default'}
      />
      {summary?.status && summary.inventory_pending && (
        <Typography variant="caption" color="text.secondary">
          File list is updating
        </Typography>
      )}
    </Stack>
  );
}
